"use client";

import { createContext, useContext, useMemo, useRef, useReducer, type ReactNode } from "react";
import { createApiClient, type ApiClientConfig, type AuthTokens } from "./client";

type ApiClient = ReturnType<typeof createApiClient>;
const ApiClientContext = createContext<ApiClient | null>(null);

export const CART_TOKEN_STORAGE_KEY = "ntr_cart_token";

/** Persist the backend-issued cart token so subsequent requests carry it (guest carts). */
export function persistCartToken(token: string) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(CART_TOKEN_STORAGE_KEY, token);
}

interface AuthHolder {
  getToken: () => string | undefined;
  getRefreshToken: () => string | undefined;
  /** Set the access token only (refresh token left unchanged). Kept for compatibility. */
  setToken: (token: string | undefined) => void;
  /** Set access + refresh together (login, signup-complete, refresh rotation). */
  setTokens: (accessToken?: string, refreshToken?: string) => void;
  /** Wipe all in-memory auth (logout / session expiry). */
  clear: () => void;
}

const AuthHolderContext = createContext<AuthHolder | null>(null);

export interface ApiClientProviderProps {
  children: ReactNode;
  baseUrl: string;
  tenantCode?: string;
  defaultLocale?: string;
  cartTokenStorageKey?: string;
  /**
   * Called once when a session that had auth can no longer be refreshed. The app
   * should show a single "session expired" message and redirect to login. Auth
   * state is already cleared by the provider before this runs.
   */
  onSessionExpired?: () => void;
}

export function ApiClientProvider({
  children,
  baseUrl,
  tenantCode = "nutrayan",
  defaultLocale = "en-IN",
  cartTokenStorageKey = "ntr_cart_token",
  onSessionExpired,
}: ApiClientProviderProps) {
  // Tokens live in a ref so the client's getters always read the latest value
  // synchronously — a retried request after refresh must never see a stale token.
  // A forced re-render notifies React consumers (e.g. `useCurrentUser` enablement).
  const tokensRef = useRef<{ access: string | undefined; refresh: string | undefined }>({
    access: undefined,
    refresh: undefined,
  });
  const [, notify] = useReducer((c: number) => c + 1, 0);
  const onSessionExpiredRef = useRef(onSessionExpired);
  onSessionExpiredRef.current = onSessionExpired;

  const auth = useMemo<AuthHolder>(
    () => ({
      getToken: () => tokensRef.current.access,
      getRefreshToken: () => tokensRef.current.refresh,
      setToken: (token) => {
        tokensRef.current = { ...tokensRef.current, access: token };
        notify();
      },
      setTokens: (accessToken, refreshToken) => {
        tokensRef.current = { access: accessToken, refresh: refreshToken };
        notify();
      },
      clear: () => {
        tokensRef.current = { access: undefined, refresh: undefined };
        notify();
      },
    }),
    [],
  );

  // `auth` is stable, so the client is created once — no stale closures on retry.
  const client = useMemo<ApiClient>(() => {
    const config: ApiClientConfig = {
      baseUrl,
      tenantCode,
      defaultLocale,
      getAuthToken: () => auth.getToken(),
      getRefreshToken: () => auth.getRefreshToken(),
      getCartToken: () => {
        if (typeof window === "undefined") return undefined;
        return window.localStorage.getItem(cartTokenStorageKey) ?? undefined;
      },
      onAuthRefreshed: (tokens: AuthTokens) => {
        auth.setTokens(tokens.accessToken, tokens.refreshToken ?? auth.getRefreshToken());
      },
      onAuthCleared: () => {
        auth.clear();
        onSessionExpiredRef.current?.();
      },
    };
    return createApiClient(config);
  }, [baseUrl, tenantCode, defaultLocale, auth, cartTokenStorageKey]);

  return (
    <ApiClientContext.Provider value={client}>
      <AuthHolderContext.Provider value={auth}>{children}</AuthHolderContext.Provider>
    </ApiClientContext.Provider>
  );
}

export function useApiClient() {
  const ctx = useContext(ApiClientContext);
  if (!ctx) throw new Error("useApiClient must be used inside <ApiClientProvider>");
  return ctx;
}

export function useAuthToken() {
  const ctx = useContext(AuthHolderContext);
  if (!ctx) throw new Error("useAuthToken must be used inside <ApiClientProvider>");
  return ctx;
}
